import { audio } from '@/audio-singleton';
import { gameSignals } from '@/bridge/gameSignals';
import { formatShareGrid, deriveShareMarker } from '@/lib/share-grid';
import { computeBadges, BADGES } from '@/lib/badges';
import { t } from '@/i18n';
import { pushToast } from '@/notifications';
import { BottomSheet } from './BottomSheet';
import { ShareIcon, CopyIcon } from './icons';

export interface ShareModalProps { open: boolean; onClose: () => void }

function buildShareText(): string {
  const num = gameSignals.puzzleNumber.value;
  const record = gameSignals.record.value;
  const streak = gameSignals.streak.value;
  if (num === null || !record || !streak) return '';
  const ids = computeBadges(record.shots);
  const localizedBadges = ids.map((id) => {
    const meta = BADGES.find((b) => b.id === id)!;
    return { id, emoji: meta.emoji, name: t(meta.i18nKey as never) };
  });
  const marker = deriveShareMarker(record);
  return formatShareGrid({
    puzzleNumber: num,
    outcomes: record.shots.map((s) => s.outcome),
    streak: streak.current,
    domain: 'napi11es.com',
    streakLabel: t('share.streakLabel'),
    badges: localizedBadges,
    ...(marker !== undefined && { shareMarker: marker }),
  });
}

export function ShareModal({ open, onClose }: ShareModalProps) {
  const text = buildShareText();
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function onShare() {
    try { await navigator.share!({ text }); }
    catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      pushToast(t('share.shareError'), 'error');
    }
  }

  async function onCopy() {
    if (!navigator.clipboard?.writeText) {
      pushToast(t('share.copyUnavailable'), 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      pushToast(t('share.copySuccess'));
    } catch {
      pushToast(t('share.copyError'), 'error');
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} ariaLabel={t('share.title')}>
      <div class="px-5 pb-5">
        <h2 class="text-lg font-bold mb-3">{t('share.title')}</h2>
        <pre class="font-mono text-sm whitespace-pre-wrap bg-gray-100 rounded-lg p-3 mb-4">{text}</pre>
        <div class="flex gap-3">
          {canShare && (
            <button class="flex-1 bg-[#1a3a5c] text-white rounded-lg px-3 py-2 flex items-center justify-center gap-2" onClick={() => { audio().play('tap'); onShare(); }}>
              <ShareIcon size={18} /> {t('share.shareButton')}
            </button>
          )}
          <button class="flex-1 bg-gray-200 text-[#222] rounded-lg px-3 py-2 flex items-center justify-center gap-2" onClick={() => { audio().play('tap'); onCopy(); }}>
            <CopyIcon size={18} /> {t('share.copyButton')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
